import * as React from 'react';
import { useEffect } from 'react';
import { articlesClientApi } from '../../../api/articlesClientApi';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { AmorgAdminPageProvider } from '../AmorgAdminPageContext';
import { H1, H2 } from '../../../designSystem/typography/Heading';
import { ArticlesTab } from './_components/ArticlesTab';
import { Icon } from '../../../designSystem/icons/Icons';
import { Button } from '../../../designSystem/button/Button';
import styled from 'styled-components';
import { ArticleList, ArticleTypeListing, rng, stringUtil } from '../../../amorg-common';
import { EditableText } from '../../../designSystem/compositeComponents/EditableText';
import { ListRow, ListRowWithChildren } from '../../../designSystem/compositeComponents/ListRow';
import { P, Small } from '../../../designSystem/typography/Body';
import { ButtonRow } from '../../../designSystem/button/ButtonRow';
import { AdminTab } from '../_components/AdminTab';



export default function ArticlesConfiguration (): JSX.Element 
{
    const [loading, setLoading] = React.useState(true);
    const [lists, setLists] = React.useState<ArticleList[]>([]);
    const [types, setTypes] = React.useState<ArticleTypeListing[]>([]);


    useEffect(() =>
    {
        const init = async () =>
        {
            setLists(await articlesClientApi.getLists());
            setTypes(await articlesClientApi.getTypes());
            setLoading(false);
        };
        init().catch(console.error);
    }, []);

    async function handleTypesChanged ()
    {
        setTypes(await articlesClientApi.getTypes());
    }

    async function handleListsChanged ()
    {
        setLists(await articlesClientApi.getLists());
    }

    return (
        <AmorgAdminPageProvider>
            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout
                noDivider
                headerContainer={
                    <Flex flexDirection="column" gap={16}>
                        <AdminTab selectedTab='articles' />
                        <ArticlesTab selectedTab='configuration' />
                    </Flex>
                }

                contentContainer={

                    <Flex flexDirection='column' gap={16}>

                        <H1>Opsætning</H1>

                        <TypeList types={types} onChanged={handleTypesChanged} />

                        <ListsList lists={lists} onChanged={handleListsChanged} />

                    </Flex>
                } />

        </AmorgAdminPageProvider>
    );
}

type TypeList = {
    types?: ArticleTypeListing[],
    onChanged: () => void;
};

function TypeList (props: TypeList): JSX.Element 
{
    async function handleClickCreateType ()
    {
        const types = await articlesClientApi.getTypes();
        const name = stringUtil.generateNextSequenceName('Ny type', types.map(t => t.name));
        await articlesClientApi.createType(rng.getRandomId(), name);
        props.onChanged();
    }

    return (
        <Flex flexDirection='column'>
            <ListRowWithChildren
                onCollapse={() => { return; }}
                styleVariant="dark"
                borderVariant="sharp"
                content={
                    <Flex justifyContent="space-between" alignItems='center'>
                        <Flex alignItems="center" gap={16}>
                            <Icon name="facility_serverRoom" color="white" /><H2 color="white">Lovtyper</H2>
                        </Flex>
                        <Small color="white">{props.types.length} typer</Small>
                    </Flex>
                }
                childRows={
                    <ListContainer>
                        {props.types.map(t =>
                            <TypeRow key={t.id} {...t} onChanged={props.onChanged} />
                        )}
                        <ButtonRow marginTop={8} marginRight={8} marginBottom={8} variant='right-aligned'>
                            <Button icon="actions_plus" variant='filled' text='Opret lovtype' onClick={handleClickCreateType} />
                        </ButtonRow>

                    </ListContainer>
                }
            />
        </Flex>

    );
}

type TypeRowProps = {
    id: string,
    name: string,
    articleCount: number,
    onChanged: () => void;
};
function TypeRow (props: TypeRowProps): JSX.Element
{
    const [error, setError] = React.useState('');

    async function handleUpdate (value: string)
    {
        const result = await articlesClientApi.updateType(props.id, value);
        if (result.status === 'success')
        {
            props.onChanged();
            setError('');
            return true;
        }
        else
        {
            if (result.clientMessage === 'invalid name format')
                setError('Ugyldigt navn');
            else if (result.clientMessage === 'name in use')
                setError('Navn i brug');
            else
                setError('Ukendt fejl');
            return false;
        }
    }

    async function handleDelete ()
    {
        await articlesClientApi.deleteType(props.id);
        props.onChanged();
    }

    return (
        <ListRow
            key={props.id}
            borderVariant='sharp'
            sizeVariant="slim"
            styleVariant={props.articleCount > 0 ? 'normal' : 'neutral'}
        >
            <Flex justifyContent={"space-between"} alignItems='center'>

                <Flex gap={16} alignItems='center'>
                    <P><EditableText styleMode="default" ariaLabel='typenavn' placeholder='' value={props.name} onSaveWithReturnValue={handleUpdate} inErrorState={error !== ''} /></P>
                    {error &&
                        <Small color="error">{error}</Small>
                    }
                </Flex>

                <Flex gap={8} alignItems='center'>
                    <Small>(Brugt af {props.articleCount} lovkrav)</Small>
                    <Button icon="interface_actions_delete" variant="destructive-text" toolTipText="Slet" disabled={props.articleCount > 0} onClick={handleDelete} />
                </Flex>
            </Flex>
        </ListRow>
    );
}



type ListsListProps = {
    lists?: ArticleList[],
    onChanged: () => void;
};

function ListsList (props: ListsListProps): JSX.Element 
{
    async function handleClickCreateList ()
    {
        const lists = await articlesClientApi.getLists();
        const name = stringUtil.generateNextSequenceName('Ny lovkravsliste', lists.map(l => l.name));
        await articlesClientApi.createList(rng.getRandomId(), name);
        props.onChanged();
    }

    return (
        <Flex flexDirection='column'>
            <ListRowWithChildren
                onCollapse={() => { return; }}
                styleVariant="dark"
                borderVariant="sharp"

                content={
                    <Flex justifyContent="space-between" alignItems='center'>
                        <Flex alignItems="center" gap={8}>
                            <Icon name="list" color="white" /><H2 color="white">Lovkravslister</H2>
                        </Flex>

                        <Small color="white">{props.lists.length} lister</Small>

                    </Flex>
                }
                childRows={
                    <ListContainer>
                        {props.lists.map(l =>
                            <ArticleListRow key={l.id} list={l} onChanged={props.onChanged} />
                        )}

                        <ButtonRow marginTop={8} marginRight={8} marginBottom={8} variant='right-aligned'>
                            <Button icon="actions_plus" text="Opret liste" variant="filled" onClick={handleClickCreateList} />
                        </ButtonRow>

                    </ListContainer>
                }
            />
        </Flex>
    );
}

type ArticleListRowProps = {
    list: ArticleList,
    onChanged: () => void;
};

function ArticleListRow (props: ArticleListRowProps): JSX.Element 
{
    const [expanded, setExpanded] = React.useState(false);
    const [error, setError] = React.useState('');

    async function handleUpdate (value: string)
    {
        const result = await articlesClientApi.updateList(props.list.id, value);
        if (result.status === 'success')
        {
            props.onChanged();
            setError('');
            return true;
        }
        else
        {
            if (result.clientMessage === 'invalid name format')
                setError('Ugyldigt navn');
            else if (result.clientMessage === 'list name in use')
                setError('Navn i brug');
            else
                setError('Ukendt fejl');
            return false;
        }
    }

    async function handleDelete ()
    {
        await articlesClientApi.deleteList(props.list.id);
        props.onChanged();
    }

    async function handleClickCreateTopic ()
    {
        const lists = await articlesClientApi.getLists();
        const name = stringUtil.generateNextSequenceName('Nyt emne', lists.flatMap(l => l.topics.map(t => t.name)));
        await articlesClientApi.createListTopic(props.list.id, rng.getRandomId(), name);
        props.onChanged();
        setExpanded(true);
    }

    const articleCount = props.list.topics.reduce((acc, t) => acc + t.articleCount, 0);

    return (


        <Flex flexDirection='column'>
            <ListRowWithChildren
                onCollapse={(val) => { setExpanded(val); }}
                sizeVariant="slim"
                styleVariant="normal"
                borderVariant='sharp'
                expanded={expanded}
                content={
                    <Flex justifyContent={"space-between"} alignItems='center'>
                        <Flex alignItems='center' gap={8} justifyContent='flex-start'>

                            <Small>#{props.list.id}</Small>

                            <P><EditableText styleMode="default" ariaLabel='listenavn' placeholder='' value={props.list.name} onSaveWithReturnValue={handleUpdate} inErrorState={error !== ''} /></P>

                            {error &&
                                <Small color="error">{error}</Small>
                            }

                        </Flex>

                        <Flex gap={16}>
                            <Flex gap={8}>
                                <Button
                                    icon="interface_actions_delete"
                                    variant="destructive-text"
                                    toolTipText="Slet"
                                    disabled={articleCount > 0 || props.list.topics.length > 0}
                                    onClick={handleDelete} />
                                <Button
                                    icon="add_circle"
                                    variant="text"
                                    toolTipText="Opret emne"
                                    onClick={handleClickCreateTopic} />
                            </Flex>
                        </Flex>
                    </Flex>
                }

                childRows={
                    props.list.topics.map(t =>
                        <TopicRow key={t.id} {...t} onChanged={props.onChanged} />
                    )
                }
            />
        </Flex>
    );
}

type TopicRowProps = {
    id: string,
    name: string,
    articleCount: number,
    onChanged: () => void;
};
function TopicRow (props: TopicRowProps): JSX.Element
{
    const [error, setError] = React.useState('');

    async function handleUpdate (value: string)
    {
        const result = await articlesClientApi.updateListTopic(props.id, value);
        if (result.status === 'success')
        {
            props.onChanged();
            setError('');
            return true;
        }
        else
        {
            if (result.clientMessage === 'invalid name format')
                setError('Ugyldigt navn');
            else if (result.clientMessage === 'name in use')
                setError('Navn i brug');
            else
                setError('Ukendt fejl');
            return false;
        }
    }

    async function handleDelete ()
    {
        await articlesClientApi.deleteListTopic(props.id);
        props.onChanged();
    }

    return (
        <ListRow key={props.id}
            sizeVariant='slim'
            borderVariant='sharp'
            styleVariant='bright'>

            <Flex justifyContent={"space-between"} alignItems='center'>

                <Flex gap={16} alignItems='center'>
                    <P><EditableText styleMode="default" ariaLabel='emnenavn' placeholder='' value={props.name} onSaveWithReturnValue={handleUpdate} inErrorState={error !== ''} /></P>
                    {error &&
                        <Small color='error'>{error}</Small>
                    }
                </Flex>
                <Flex gap={16} alignItems='center'>
                    <Small>(Brugt af {props.articleCount} lovkrav)</Small>
                    <ButtonRow marginRight={88}>
                        <Button icon="interface_actions_delete" variant="destructive-text" toolTipText="Slet"
                            disabled={props.articleCount > 0}
                            onClick={handleDelete}
                        />

                    </ButtonRow>
                </Flex>
            </Flex>
        </ListRow>
    );
}

const ListContainer = styled.div`
    border: 1px solid black;
`;